import React, { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { TouchableOpacity } from 'react-native';

import { Text } from '@/components/TextComponent';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { useLaunchIntercomMessenger } from '@/data/intercom/intercom';
import BetrAnalytics from '@/feature/analytics/analytics';
import { AnalyticsEvent } from '@/feature/analytics/constants';
import { selectAllWithdrawMethods, useWithdrawalMethods } from '@/feature/withdraw/hooks/use-withdrawal-methods';
import { usePaymentMethodEnabled } from '@/hooks/payments/use-payment-method-enabled';
import { usePaymentMethodTypeEnabled } from '@/hooks/payments/use-payment-method-type-enabled';
import { useActiveWallet } from '@/hooks/use-active-wallet';
import {
    canWithdrawSelector,
    hasMadeFirstDepositSelector,
    hasUserPurchasedSelector,
    useAuthUserConfig,
} from '@/hooks/use-auth-user-config';
import { useHasSocialWallet } from '@/hooks/use-has-social-wallet';
import { useSocialWalletTranslation } from '@/hooks/use-social-wallet-translation';

const WithdrawUnavailable = () => {
    const { t } = useTranslation('wallet');
    const tSocialWallet = useSocialWalletTranslation('wallet');
    const hasSocialWallet = useHasSocialWallet();
    const { launchIntercomMessenger } = useLaunchIntercomMessenger();

    return (
        <Box>
            <Text mb="s8" color={ds.colorToken.text.secondary}>
                {tSocialWallet('withdrawal_insufficient_funds')}
            </Text>
            <Text ml="s8" color={ds.colorToken.text.secondary}>
                1. {tSocialWallet('withdrawal_insufficient_funds_first_bullet')}
            </Text>
            <Text ml="s8" color={ds.colorToken.text.secondary}>
                2. {tSocialWallet('withdrawal_insufficient_funds_second_bullet')}
            </Text>
            <Text ml="s8" color={ds.colorToken.text.secondary}>
                3. {t('withdrawal_insufficient_funds_third_bullet')}
            </Text>
            <Text ml="s8" color={ds.colorToken.text.secondary}>
                4. {tSocialWallet('withdrawal_insufficient_funds_fourth_bullet')}
            </Text>
            <Text ml="s8" mb="s8" color={ds.colorToken.text.secondary}>
                5. {tSocialWallet('withdrawal_insufficient_funds_fifth_bullet')}
            </Text>
            <Text color={ds.colorToken.text.secondary}>
                <Trans
                    i18nKey={
                        hasSocialWallet
                            ? 'withdrawal_insufficient_funds_contact_support_social'
                            : 'withdrawal_insufficient_funds_contact_support'
                    }
                    ns="wallet"
                >
                    If you have an active deposit match promotion but wish to withdraw your original deposit you will
                    forfeit your deposit match bonus,
                    <TouchableOpacity onPress={launchIntercomMessenger}>
                        <Text lineHeight={16} textDecorationLine="underline" color={ds.colorToken.text.secondary}>
                            contact support
                        </Text>
                    </TouchableOpacity>
                    to proceed
                </Trans>
            </Text>
        </Box>
    );
};

export const useWithdrawalStatus = () => {
    const tSocialWallet = useSocialWalletTranslation('wallet');

    const { isSharedSocialWallet } = useActiveWallet();

    const { balance } = useActiveWallet();

    const { data: hasSuccessfulDeposit } = useAuthUserConfig({
        select: hasMadeFirstDepositSelector,
    });
    const { data: hasSuccessfulPurchase } = useAuthUserConfig({
        select: hasUserPurchasedSelector,
    });
    const { data: userCanWithdraw, isLoading: isUserConfigLoading } = useAuthUserConfig({
        select: canWithdrawSelector,
    });
    const { data: withdrawMethods = [], isLoading: isWithdrawMethodsLoading } = useWithdrawalMethods({
        select: selectAllWithdrawMethods,
    });

    const { enableWithdrawals } = usePaymentMethodEnabled();
    const isPaymentMethodTypeEnabled = usePaymentMethodTypeEnabled();

    /**
     * Check to ensure that the withdrawal methods are enabled on prismic
     */
    const enabledWithdrawMethods = useMemo(() => {
        const availableWithdrawalMethods = withdrawMethods.filter(
            method => !method.via_site_id || (method.via_site_id && !method.closed_loop_completed)
        );

        return availableWithdrawalMethods.filter(method => isPaymentMethodTypeEnabled(method.type));
    }, [withdrawMethods, isPaymentMethodTypeEnabled]);

    const hasWithdrawMethods = enabledWithdrawMethods && enabledWithdrawMethods.length > 0;
    const hasSuccessfulDepositOrPurchase = isSharedSocialWallet ? hasSuccessfulPurchase : hasSuccessfulDeposit;

    const canWithdraw = balance && userCanWithdraw && enableWithdrawals && hasSuccessfulDepositOrPurchase;
    const isLoading = isUserConfigLoading || isWithdrawMethodsLoading;

    return {
        canWithdraw,
        hasWithdrawMethods,
        isLoading,
        infoSheetParams: {
            title: tSocialWallet('withdrawal_unavailable_title'),
            description: <WithdrawUnavailable />,
            buttonLabel: 'Close',
            handlePress: () => {
                BetrAnalytics.trackProductEvent(AnalyticsEvent.WITHDRAWAL_UNAVAILABLE_CLOSE);
            },
        },
    };
};
